/**
 * THE merge of the two supplier tiers into one picker list.
 *
 * ── THE TWO TIERS ────────────────────────────────────────────────────
 * Product owner, 2026-08-08: *"Suppliers should come from orders which have been
 * approved by the buyer so that the supplier names are correct. BUT the supplier
 * names should be just for the drop down and NOT added to the supplier list.
 * Only the buyer/supervisor can create suppliers in admin settings."*
 *
 *   RECORD     — a real `suppliers` row from `GET /api/suppliers`. It has an
 *                account number, a contact, a phone. Created only through admin
 *                settings by a caller holding `canManageDepartmentSettings`.
 *   SUGGESTION — a name typed on a past order that reached approval, from
 *                `GET /api/suppliers/name-suggestions`. Convenience only.
 *                🚨 It is NOT in the `suppliers` table and picking one does not
 *                put it there — `supplier_name` on an order is free text, and
 *                that is the whole design.
 *
 * ── WHY THE DISTINCTION IS RENDERED, NOT JUST MODELLED ───────────────
 * Both tiers are the same thing to the user until they pick one: a string that
 * goes in the Supplier box. But one of them means "we have an account with these
 * people" and the other means "somebody typed this once". `isRecord` is what
 * lets the picker say which — see `components/SupplierOptionList.js` for the
 * marker itself and for why only ONE tier carries it. (This line cited
 * `OrderForm.js` until 2026-08-08; the marker has never lived there, and
 * OrderForm is a CONSUMER of the list component, not its owner.)
 *
 * ── THE KEY RULE IS A MIRROR ─────────────────────────────────────────
 * `supplierOptionKey` mirrors `supplierKey` in `server/helpers/supplierName.js`:
 * collapse whitespace runs to one space, trim, lowercase. It exists here so a
 * suggestion can be deduped against a record without a round trip, and so the
 * dropdown filters on the same notion of sameness the server folded with.
 *
 * 🔑 **IT IS A CONVENIENCE AND NEVER A GATE.** The server folds independently
 * and is the authority; nothing here decides who may do anything. Held in step
 * with the server by a shared case table — `client/src/utils/supplierOptions.test.js`
 * and §7 of `server/tests/api/supplier-name-suggestions.test.js` run the SAME
 * inputs, so the two suites cannot both be green while the rules disagree. Same
 * device, and the same justification, as `utils/poNumber.js`.
 *
 * 🪤 Do NOT reach for `localeCompare` or `toLocaleLowerCase` here: the server
 * folds with plain `toLowerCase()` precisely because SQLite's `LOWER()` is
 * ASCII-only while PostgreSQL's is locale-aware, and a locale-sensitive client
 * would be a fourth answer to a question that already has one.
 */

/** The case-insensitive identity of a supplier name. Mirrors the server rule. */
export function supplierOptionKey(raw) {
  if (raw === null || raw === undefined) return '';
  return String(raw).replace(/\s+/g, ' ').trim().toLowerCase();
}

/**
 * Merge supplier RECORDS and past-order SUGGESTIONS into one list for a picker.
 *
 * Records come first and keep their incoming order (the server sends them
 * `ORDER BY name`); suggestions follow in the order the server folded them into,
 * which is most-used first. **Records first is deliberate and is not cosmetic**:
 * where a name exists in both tiers the record is the one worth having, so it
 * must be the one the user's eye lands on — and the suggestion for that same
 * name is dropped entirely rather than rendered twice.
 *
 * Blank names are dropped. A record with no usable name is dropped rather than
 * rendered as an empty row that selects an empty string.
 *
 * @param {{id?: number, name: string}[]} suppliers   rows from GET /api/suppliers
 * @param {{name: string, order_count?: number}[]} suggestions  rows from
 *        GET /api/suppliers/name-suggestions
 * @returns {{name: string, key: string, isRecord: boolean, supplier: object|null,
 *            orderCount: number|null}[]}
 */
export function buildSupplierOptions(suppliers = [], suggestions = []) {
  const options = [];
  const seen = new Set();

  for (const s of suppliers || []) {
    const name = typeof s?.name === 'string' ? s.name.trim() : '';
    if (!name) continue;
    const key = supplierOptionKey(name);
    if (seen.has(key)) continue;
    seen.add(key);
    options.push({ name, key, isRecord: true, supplier: s, orderCount: null });
  }

  for (const s of suggestions || []) {
    const name = typeof s?.name === 'string' ? s.name.trim() : '';
    if (!name) continue;
    const key = supplierOptionKey(name);
    // A name that is already a RECORD never also appears as a suggestion: the
    // record is strictly more informative, and two rows reading "Flints" is
    // exactly the proliferation this feature exists to stop.
    if (seen.has(key)) continue;
    seen.add(key);
    options.push({
      name,
      key,
      isRecord: false,
      supplier: null,
      orderCount: Number.isFinite(Number(s?.order_count)) ? Number(s.order_count) : null,
    });
  }

  return options;
}

/**
 * The typed-query filter — substring, case- and whitespace-insensitive.
 *
 * 🔑 ONE matcher, read by BOTH the list and the "nothing to show" hint beside
 * it. `OrderForm.js` already records why: two copies of the same filter
 * expression that can disagree about what "matches" means is how a box comes to
 * show neither a list nor a reason.
 *
 * An empty query matches NOTHING, preserving the supplier box's existing
 * behaviour — the list opens only once you have typed something. (The gag
 * autocomplete on the same form deliberately does the opposite; they are not
 * the same control and must not be unified.)
 */
export function matchSupplierOptions(options = [], query) {
  const q = supplierOptionKey(query);
  if (!q) return [];
  return (options || []).filter((o) => o.key.includes(q));
}

// ── ARCHAEOLOGY (moved from root CLAUDE.md 2026-09-25) ──────────────────────
// The root `CLAUDE.md` Shared Modules row for this file and
// `components/SupplierOptionList.js` keeps every RULE (only the suggestion tier
// is marked, the mirror is never a gate, no per-keystroke fetch) and points
// here. Moved VERBATIM from that row:
//
// Marking the records instead was rejected on the measured shape of the data
// (in the dev database only 2 of 147 departments hold any `suppliers` rows at
// all, while department 3 had 258 orders using 17 distinct typed names and ZERO
// records), so marking records would badge almost nothing and leave every row
// in a typical department unexplained.
//
// ... and `pages/AdminSettings.js` was **re-graded** there from `NO_SEARCH_BOX`
// (its old reason said in terms that its Suppliers list had no search, which
// this change falsified).
